import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronRight, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import scheduleService from '../../../services/community/scheduleService';
import { CreateScheduleBody, ScheduleModel } from '../../../models/schedule.model';
import { CommunityFeature } from '../../../types/community';
import PrimaryOverlay from '../../components/overlays/PrimaryOverlay';
import PremiumRequiredCard from '../../components/cards/PremiumRequiredCard';
import ModalLayout from '../../components/layouts/ModalLayout';
import Button from '../../components/buttons/Button';
import Input from '../../components/misc/Input';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import Slider from '../../components/misc/Slider';

export default function SchedulesTab(): JSX.Element {
  const { t } = useTranslation();
  const community = useSelectedCommunity();
  const navigate = useNavigate();

  const [schedules, setSchedules] = useState<ScheduleModel[]>([]);
  const [loading, setLoading] = useState(false);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formAskForResponses, setFormAskForResponses] = useState(false);

  const [saving, setSaving] = useState(false);

  const hasSchedulesFeature = community?.features?.[CommunityFeature.SCHEDULES];

  useEffect(() => {
    if (community && hasSchedulesFeature) {
      loadSchedules();
    }
  }, [community, hasSchedulesFeature]);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      const data = await scheduleService.listSchedules();
      setSchedules(data);
    } catch {
      toast.error(t('general.error'));
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setFormName('');
    setFormDescription('');
    setFormAskForResponses(false);
    setCreateModalOpen(true);
  };

  const handleCreate = async () => {
    if (!formName.trim()) return;

    try {
      setSaving(true);
      const body: CreateScheduleBody = {
        name: formName.trim(),
        description: formDescription.trim() || undefined,
        askForResponses: formAskForResponses,
      };
      const created = await scheduleService.createSchedule(body);
      setSchedules((prev) => [{ ...created, shiftCount: created.shiftCount ?? 0 }, ...prev]);
      setCreateModalOpen(false);
      toast.success(t('page.event.schedules.messages.schedule.created'));
    } catch {
      toast.error(t('general.error'));
    } finally {
      setSaving(false);
    }
  };

  if (!hasSchedulesFeature) {
    return (
      <div className="relative h-[60vh] overflow-hidden">
        <PrimaryOverlay>
          <PremiumRequiredCard
            description={t('page.onboarding.communityFeaturesScreen.features.schedules.description')}
            buttonText={t('general.upgrade')}
            onClick={() => navigate('/settings/subscriptions')}
          />
        </PrimaryOverlay>
        <div className="pointer-events-none opacity-30">
          <ScheduleListSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-xl font-semibold text-primary-900">
          {t('page.event.schedules.title')}
        </h3>
        <Button
          variant="primary"
          onClick={openCreateModal}
          className="flex items-center gap-2">
          <FaPlus className="h-4 w-4" />
          {t('page.event.schedules.createSchedule')}
        </Button>
      </div>

      {loading ? (
        <ScheduleListSkeleton />
      ) : schedules.length === 0 ? (
        <EmptyScreenView subtitle={t('page.event.schedules.emptySchedules')} />
      ) : (
        <div className="flex flex-col gap-3">
          {schedules.map((schedule) => (
            <div
              key={schedule.scheduleId}
              className="flex items-center justify-between rounded-lg bg-secondary-200 px-4 py-3 transition-colors hover:bg-opacity-80">
              <button
                type="button"
                className="min-w-0 flex-1 cursor-pointer text-left"
                onClick={() => navigate(`/events/schedule/${schedule.scheduleId}`)}>
                <div className="flex flex-col gap-1">
                  <span className="font-medium text-primary-900">{schedule.name}</span>
                  {schedule.description && (
                    <span className="truncate text-sm text-primary-500">
                      {schedule.description}
                    </span>
                  )}
                  <span className="text-xs">
                    {t('page.event.schedules.shiftCount', { count: schedule.shiftCount ?? 0 })}
                  </span>
                </div>
              </button>
              <div className="ml-4 flex items-center gap-2">
                <FaChevronRight className="pointer-events-none h-4 w-4" />
              </div>
            </div>
          ))}
        </div>
      )}

      <ModalLayout
        closeButton
        open={createModalOpen}
        setOpen={setCreateModalOpen}
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[700px]">
        <div className="flex min-w-[320px] flex-col gap-4">
          <h4 className="text-lg font-semibold text-primary-900">
            {t('page.event.schedules.createSchedule')}
          </h4>
          <Input
            id="schedule-name"
            label={t('page.event.schedules.fields.scheduleName')}
            type="text"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            required
          />
          <Input
            id="schedule-description"
            label={t('page.event.schedules.fields.scheduleDescription')}
            type="textarea"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
          />
          <Slider
            label={t('page.event.schedules.fields.askForResponses')}
            items={[
              { label: t('general.no'), value: false },
              { label: t('general.yes'), value: true },
            ]}
            value={formAskForResponses}
            onChange={setFormAskForResponses}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCreateModalOpen(false)}>
              {t('general.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={!formName.trim() || saving}>
              {t('general.save')}
            </Button>
          </div>
        </div>
      </ModalLayout>
    </div>
  );
}

function ScheduleListSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className="h-16 rounded-lg border bg-secondary-200 animate-pulse"
        />
      ))}
    </div>
  );
}
